import React, { useCallback, useState, useMemo } from 'react';
import { useCanvasStore } from '@/stores/canvasStore';
import { Home, Folder } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CanvasControl, CanvasControlGroup, CONTROL_ICON } from './CanvasControl';
import { TextFormattingSidebar } from '@/components/sidebar/TextFormattingSidebar';
import { RAIL_SURFACE } from '@/components/sidebar/RailButton';
import { useDockMagnify } from '@/components/toolbar/useDockMagnify';
import { BoardsFanCard } from './BoardsFanCard';

export function FloatingHomeButton() {
  const { currentBoardId, goToFocusLanding, viewMode, activeEditor, isEditingItem, boards } = useCanvasStore();
  const [showFanCard, setShowFanCard] = useState(false);
  // The text formatting rail sits on the left edge: hovered glyphs rise to the right
  const textRail = useDockMagnify<HTMLDivElement>({ rise: 'right' });

  // Check if we're at the home board
  const isAtHome = useMemo(() => {
    const homeBoard = boards.find(b => b.parent_id === null);
    return homeBoard && currentBoardId === homeBoard.id;
  }, [boards, currentBoardId]);

  const handleHomeClick = useCallback(() => {
    goToFocusLanding();
  }, [goToFocusLanding]);

  // Only show when in Focus mode with a board selected
  if (viewMode !== 'focus' || !currentBoardId) return null;

  const showTextSidebar = isEditingItem && activeEditor;
  const showSidebar = showTextSidebar;

  return (
    <>
      {/* Home and Boards buttons - nudge right when sidebar is open */}
      <CanvasControlGroup
        rise="right"
        className={cn(
          'fixed top-[4.5rem] z-40 transition-all duration-300',
          showSidebar ? 'left-[4.75rem]' : 'left-4',
        )}
      >
        <CanvasControl label="Create Home" side="right" active={isAtHome} onClick={handleHomeClick}>
          <Home className={CONTROL_ICON} />
        </CanvasControl>
        <CanvasControl label="Recent boards" side="right" active={showFanCard} onClick={() => setShowFanCard(!showFanCard)}>
          <Folder className={CONTROL_ICON} />
        </CanvasControl>
      </CanvasControlGroup>

      {/* Boards Fan Card - positioned next to the button stack */}
      {showFanCard && (
        <>
          {/* Invisible backdrop to close on click outside */}
          <div 
            className="fixed inset-0 z-30" 
            onClick={() => setShowFanCard(false)} 
          />
          <div 
            className={cn(
              "fixed top-[4.5rem] z-40 transition-all duration-300",
              showSidebar ? "left-[7.75rem]" : "left-16"
            )}
          >
            <BoardsFanCard onClose={() => setShowFanCard(false)} />
          </div>
        </>
      )}

      {/* Text Formatting Sidebar - slides in from left when editing text */}
      <div 
        ref={textRail.ref}
        {...textRail.handlers}
        className={cn(
          "fixed top-[4.5rem] left-4 z-40 transition-all duration-300",
          RAIL_SURFACE,
          // Thirteen buttons outgrow a short laptop screen: scroll rather than run into the bottom bar
          "max-h-[calc(100dvh-10rem)] overflow-y-auto scrollbar-hide",
          showTextSidebar 
            ? "opacity-100 translate-x-0" 
            : "opacity-0 -translate-x-full pointer-events-none"
        )}
      >
        <TextFormattingSidebar editor={activeEditor} />
      </div>

    </>
  );
}
